/**
 * CRM Deal Detail — deal overview with line items, stage progression, and related data.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Badge, Tag, Button, EmptyState } from '../../components/ui';
import { DetailHeader, DetailSection, FieldGrid, Field } from '../../components/detail';
import { DollarSign, ArrowRight, Building2, Users, Clock } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { timeAgo } from '../../lib/utils';
import { DEAL_STAGE_TONE } from './deal-format';
import { fetchDealDetail, updateDeal } from '../../lib/crm-api';
import type { CrmDeal, CrmDealItem, CrmCompany, CrmContact, CrmActivity } from '../../lib/crm-api';
import { toast } from '../../components/ui';
import { QuoteDraftEditor } from './quote-draft-editor';

const STAGE_FLOW = ['draft', 'sent', 'negotiating', 'won'];

interface Props {
  dealId: number;
}

export function DealDetailPage({ dealId }: Props) {
  const t = useT();

  const STAGE_LABELS: Record<string, string> = {
    draft: t('crm.stageDraft'),
    sent: t('crm.stageSent'),
    negotiating: t('crm.stageNegotiating'),
    won: t('crm.stageWon'),
    lost: t('crm.stageLost'),
    cancelled: t('crm.stageCancelled'),
  };
  const [deal, setDeal] = useState<CrmDeal | null>(null);
  const [items, setItems] = useState<CrmDealItem[]>([]);
  const [company, setCompany] = useState<CrmCompany | null>(null);
  const [contact, setContact] = useState<CrmContact | null>(null);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDealDetail(dealId);
      setDeal(data.deal);
      setItems(data.items);
      setCompany(data.company);
      setContact(data.contact);
      setActivities(data.activities);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const advanceStage = async () => {
    if (!deal) return;
    const currentIdx = STAGE_FLOW.indexOf(deal.stage);
    if (currentIdx < 0 || currentIdx >= STAGE_FLOW.length - 1) return;
    const nextStage = STAGE_FLOW[currentIdx + 1];
    setAdvancing(true);
    try {
      const result = await updateDeal(deal.id, { stage: nextStage });
      setDeal(result.deal);
      toast(`Stage updated to ${STAGE_LABELS[nextStage]}`, 'success');
      // Reload to refresh activities
      loadData();
    } catch (_e) {
      toast(t('crm.failedUpdateStage'), 'error');
    } finally {
      setAdvancing(false);
    }
  };

  const markLost = async () => {
    if (!deal) return;
    setAdvancing(true);
    try {
      const result = await updateDeal(deal.id, { stage: 'lost' });
      setDeal(result.deal);
      toast(t('crm.dealMarkedLost'), 'warning');
      loadData();
    } catch {
      toast(t('crm.failedUpdateStage'), 'error');
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner className="h-6 w-6 text-fg-faint" />
      </div>
    );
  }

  if (!deal) {
    return <EmptyState icon={DollarSign} title="Deal not found" description="This deal may have been deleted" />;
  }

  const currentStageIdx = STAGE_FLOW.indexOf(deal.stage);
  const canAdvance = currentStageIdx >= 0 && currentStageIdx < STAGE_FLOW.length - 1;
  const isClosed = deal.stage === 'won' || deal.stage === 'lost' || deal.stage === 'cancelled';
  const subtotal = items.reduce((sum, i) => sum + i.line_total, 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <DetailHeader
          onBack={() => (window.location.hash = '#/crm/deals')}
          backLabel={t('crm.backToDeals')}
          titlePrefix={<span className="text-sm font-mono text-fg-muted">{deal.deal_number}</span>}
          title={deal.title}
          subtitle={
            <>
              <Tag tone={DEAL_STAGE_TONE[deal.stage] ?? 'neutral'}>{STAGE_LABELS[deal.stage] || deal.stage}</Tag>
              <Badge>{deal.type === 'quotation' ? t('crm.typeQuotation') : t('crm.typeSalesOrder')}</Badge>
              {deal.expected_close_date && (
                <span className="text-xs text-fg-muted">Expected close: {deal.expected_close_date}</span>
              )}
            </>
          }
          actions={
            <div className="text-right">
              <div className="text-2xl font-bold text-fg">${(deal.amount || 0).toLocaleString()}</div>
              <div className="text-sm text-fg-muted">{deal.currency}</div>
              {deal.probability !== null && deal.probability !== undefined && (
                <div className="text-xs text-fg-faint mt-1">{deal.probability}% probability</div>
              )}
            </div>
          }
        />

        {/* Stage progression */}
        <DetailSection title="Stage Progression">
          <div className="flex items-center gap-1 mb-4">
            {STAGE_FLOW.map((s, idx) => {
              const isActive = s === deal.stage;
              const isPast = STAGE_FLOW.indexOf(deal.stage) > idx;
              return (
                <React.Fragment key={s}>
                  <div
                    className={`flex-1 h-2 rounded-full transition-colors ${
                      isPast || isActive ? 'bg-primary-500' : 'bg-surface-muted'
                    }`}
                  />
                  {idx < STAGE_FLOW.length - 1 && <div className="w-1" />}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[10px] text-fg-muted mb-4">
            {STAGE_FLOW.map((s) => (
              <span key={s} className={s === deal.stage ? 'text-primary-fg-strong font-medium' : ''}>
                {STAGE_LABELS[s]}
              </span>
            ))}
          </div>
          {!isClosed && (
            <div className="flex items-center gap-2">
              {canAdvance && (
                <Button size="sm" variant="default" onClick={advanceStage} disabled={advancing}>
                  {advancing ? <Spinner className="h-3 w-3" /> : <ArrowRight size={14} />}
                  <span className="ml-1">Advance to {STAGE_LABELS[STAGE_FLOW[currentStageIdx + 1]]}</span>
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={markLost} disabled={advancing}>
                Mark as Lost
              </Button>
            </div>
          )}
        </DetailSection>

        {/* Related customer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {company && (
            <DetailSection title="Company">
              <button
                onClick={() => (window.location.hash = `#/crm/companies/${company.id}`)}
                className="flex items-center gap-2 text-sm text-fg-secondary hover:text-fg transition-colors"
              >
                <Building2 size={14} className="text-fg-faint" />
                <span className="font-medium">{company.name}</span>
              </button>
            </DetailSection>
          )}
          {contact && (
            <DetailSection title="Contact">
              <button
                onClick={() => (window.location.hash = `#/crm/contacts/${contact.id}`)}
                className="flex items-center gap-2 text-sm text-fg-secondary hover:text-fg transition-colors"
              >
                <Users size={14} className="text-fg-faint" />
                <span className="font-medium">{contact.name}</span>
                <span className="text-xs text-fg-muted">{contact.email}</span>
              </button>
            </DetailSection>
          )}
        </div>

        {/* Deal info: terms + pricing breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DetailSection title="Terms">
            <FieldGrid cols={1}>
              <Field label={t('crm.deliveryMethod')} value={deal.delivery_method} hideEmpty />
              <Field label={t('crm.paymentMethod')} value={deal.payment_method} hideEmpty />
              <Field label={t('crm.paymentTerms')} value={deal.payment_terms} hideEmpty />
              {deal.type === 'quotation' && <Field label={t('crm.validUntil')} value={deal.valid_until} hideEmpty />}
              <Field
                label={t('crm.expectedDelivery')}
                value={
                  deal.expected_delivery_date ||
                  (deal.expected_delivery_days ? `${deal.expected_delivery_days}d` : null)
                }
                hideEmpty
              />
              <Field label={t('crm.ourCompany')} value={deal.our_company} hideEmpty />
              <Field label={t('crm.salesRep')} value={deal.sales_rep} hideEmpty />
              <Field label={t('crm.owner')} value={deal.owner_user} hideEmpty />
              {deal.notes && <Field label={t('crm.notes')} value={deal.notes} />}
            </FieldGrid>
          </DetailSection>

          <DetailSection title="Pricing">
            <FieldGrid cols={1}>
              <Field label={t('crm.goodsSubtotal')} value={money(deal.goods_subtotal, deal.currency)} hideEmpty />
              <Field label={t('crm.freight')} value={money(deal.freight, deal.currency)} hideEmpty />
              <Field
                label={t('crm.taxAmount')}
                value={deal.tax_amount != null ? money(deal.tax_amount, deal.currency) : pct(deal.tax_rate)}
                hideEmpty
              />
              {deal.bank_fee != null && <Field label={t('crm.bankFee')} value={money(deal.bank_fee, deal.currency)} />}
              {deal.discount_amount != null && (
                <Field label={t('crm.discount')} value={money(deal.discount_amount, deal.currency)} />
              )}
              {deal.deposit_amount != null && (
                <Field
                  label={t('crm.deposit')}
                  value={`${money(deal.deposit_amount, deal.currency)}${deal.deposit_ratio != null ? ` (${Math.round(deal.deposit_ratio * 100)}%)` : ''}`}
                />
              )}
              {deal.balance_amount != null && (
                <Field label={t('crm.balance')} value={money(deal.balance_amount, deal.currency)} />
              )}
              {deal.type === 'sales_order' && deal.received_amount != null && (
                <Field label={t('crm.received')} value={money(deal.received_amount, deal.currency)} />
              )}
            </FieldGrid>
          </DetailSection>
        </div>

        {/* Ship-to */}
        {(deal.consignee_name || deal.consignee_company || deal.consignee_address) && (
          <DetailSection title={t('crm.shipTo')}>
            <FieldGrid cols={3}>
              <Field label={t('crm.company')} value={deal.consignee_company} hideEmpty />
              <Field label={t('crm.contact')} value={deal.consignee_name} hideEmpty />
              <Field label={t('crm.phone')} value={deal.consignee_phone} hideEmpty />
              <Field label={t('crm.email')} value={deal.consignee_email} hideEmpty />
              <Field label={t('crm.address')} value={deal.consignee_address} hideEmpty />
            </FieldGrid>
          </DetailSection>
        )}

        {/* Line items */}
        <DetailSection title={`Line Items (${items.length})`}>
          {items.length === 0 ? (
            <p className="text-sm text-fg-faint text-center py-4">No line items</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="text-left text-xs font-medium text-fg-muted py-2">#</th>
                    <th className="text-left text-xs font-medium text-fg-muted py-2">Product</th>
                    <th className="text-left text-xs font-medium text-fg-muted py-2 hidden sm:table-cell">
                      {t('crm.adapter')}
                    </th>
                    <th className="text-right text-xs font-medium text-fg-muted py-2">Qty</th>
                    <th className="text-right text-xs font-medium text-fg-muted py-2">Unit Price</th>
                    <th className="text-right text-xs font-medium text-fg-muted py-2 hidden sm:table-cell">Discount</th>
                    <th className="text-right text-xs font-medium text-fg-muted py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id} className="border-b border-edge last:border-b-0">
                      <td className="py-2 text-xs text-fg-muted">{idx + 1}</td>
                      <td className="py-2">
                        <span className="text-sm text-fg">{item.product_name}</span>
                        {(item.description || item.remark) && (
                          <p className="text-[10px] text-fg-faint mt-0.5">{item.description || item.remark}</p>
                        )}
                      </td>
                      <td className="py-2 text-xs text-fg-muted hidden sm:table-cell">{item.adapter || '—'}</td>
                      <td className="py-2 text-xs text-fg-secondary text-right">{item.quantity}</td>
                      <td className="py-2 text-xs text-fg-secondary text-right">${item.unit_price.toLocaleString()}</td>
                      <td className="py-2 text-xs text-fg-muted text-right hidden sm:table-cell">
                        {item.discount_percent > 0 ? `${item.discount_percent}%` : '—'}
                      </td>
                      <td className="py-2 text-sm font-medium text-fg text-right">
                        ${item.line_total.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-edge">
                    <td colSpan={6} className="py-2 text-right text-xs font-medium text-fg-muted hidden sm:table-cell">
                      Subtotal
                    </td>
                    <td colSpan={5} className="py-2 text-right text-xs font-medium text-fg-muted sm:hidden">
                      Subtotal
                    </td>
                    <td className="py-2 text-right text-sm font-bold text-fg">${subtotal.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DetailSection>

        {/* AI quote draft — frontend action target (crm_prefill_quote) */}
        <QuoteDraftEditor dealId={deal.id} currency={deal.currency} onSaved={loadData} />

        {/* Activity log */}
        {activities.length > 0 && (
          <DetailSection title="Activity Log">
            <div className="space-y-1">
              {activities.map((a) => (
                <div key={a.id} className="flex items-start gap-2 py-1.5 border-b border-edge last:border-b-0">
                  <Clock size={12} className="text-fg-faint mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-fg-secondary">
                      <span className="font-medium">{a.action.replace(/_/g, ' ')}</span>
                      {a.field_name && (
                        <span className="text-fg-muted">
                          {' '}
                          — {a.field_name}: {a.old_value || '—'} → {a.new_value || '—'}
                        </span>
                      )}
                    </span>
                  </div>
                  <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(a.created_at)}</span>
                </div>
              ))}
            </div>
          </DetailSection>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────

function money(v: number | null | undefined, currency: string): string | null {
  if (v == null) return null;
  return `${currency === 'USD' ? '$' : ''}${v.toLocaleString()}${currency !== 'USD' ? ` ${currency}` : ''}`;
}

function pct(v: number | null | undefined): string | null {
  if (v == null) return null;
  return `${(v * 100).toFixed(v < 1 ? 1 : 0)}%`;
}
